import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchAdminRisks, fetchRiskGross } from '@/api/(admin)/risk-management';
import { useTable } from '@/hooks/useTable';
import {
  buildListQs,
  type ListParams,
  normalizeListParams,
} from '../-utils/listParams';

/** Everything under this prefix — what the write paths invalidate. */
export const riskGrossQueryKey = ['management', 'risk-gross'] as const;
export const allAdminRisksQueryKey = ['management', 'all-risks'] as const;

export function riskGrossQueryOptions(params: ListParams) {
  return queryOptions({
    queryKey: [
      ...riskGrossQueryKey,
      'list',
      normalizeListParams(params),
    ] as const,
    queryFn: () => fetchRiskGross(buildListQs(params)),
  });
}

/**
 * Every risk, for the pickers.
 *
 * The gross sheet and the gross list's risk filter both need the catalogue as
 * options rather than a page of rows, so this asks for one large page — the
 * same thing the old drawer did.
 */
export function allAdminRisksQueryOptions() {
  return queryOptions({
    queryKey: allAdminRisksQueryKey,
    queryFn: () => fetchAdminRisks('page=0&count=500'),
    staleTime: 5 * 60_000,
  });
}

/**
 * What a risk × country gross index used to be.
 *
 * `isHistory` is what switches the endpoint from the rows in force to the
 * superseded ones. The country key is `countryCode`, not the `country` the
 * list's own filter uses.
 */
export function riskGrossHistoryQueryOptions(
  riskId: string | undefined,
  countryCode: string | undefined,
) {
  return queryOptions({
    queryKey: [...riskGrossQueryKey, 'history', riskId, countryCode] as const,
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('page', '0');
      qs.set('count', '50');
      qs.set(
        'filter',
        JSON.stringify({ countryCode, riskId, isHistory: true }),
      );
      return fetchRiskGross(qs.toString());
    },
    enabled: Boolean(riskId && countryCode),
  });
}

/** Gross indices, paged and filtered through the URL. */
export function useRiskGross() {
  // No `tab`: this list has its own route, so its search params are its own.
  const table = useTable('management-risk-gross', { mode: 'url' });

  const params: ListParams = {
    page: table.page,
    limit: table.limit,
    ...(table.queryParams.sort !== undefined
      ? { sort: table.queryParams.sort }
      : {}),
    filters: table.filters,
  };

  const query = useQuery({
    ...riskGrossQueryOptions(params),
    placeholderData: prev => prev,
  });

  return {
    rows: query.data?.data ?? [],
    total: query.data?.meta?.totalCount ?? 0,
    isLoading: query.isLoading,
    sorting: table.sorting,
    filters: table.filters,
    pagination: { pageIndex: table.page - 1, pageSize: table.limit },
    onSortingChange: table.onSortingChange,
    onFiltersChange: table.onFiltersChange,
    onPaginationChange: table.onPaginationChange,
  };
}

export function useAllAdminRisks(enabled = true) {
  const query = useQuery({ ...allAdminRisksQueryOptions(), enabled });
  return { risks: query.data?.data ?? [], isLoading: query.isLoading };
}

export function useRiskGrossHistory(
  riskId: string | undefined,
  countryCode: string | undefined,
) {
  const query = useQuery(riskGrossHistoryQueryOptions(riskId, countryCode));
  return { rows: query.data?.data ?? [], isLoading: query.isLoading };
}
